/**
 * Domain hooks for habit-related PowerSync queries.
 *
 * Each hook encapsulates:
 *   - The canonical `queryKey` from the centralized factory
 *   - The `queryFn` (Drizzle query builder)
 *   - The `createPowerSyncQuery` call
 *
 * Consumers import one of these hooks instead of calling
 * `createPowerSyncQuery` directly, making it impossible to create
 * duplicate subscriptions or mismatched query keys.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@habits/powersync";

import {
  type CompletionRow,
  getAllCompletionsQuery,
  getHabitsQuery,
  getHabitVersionsQuery,
  getRemindersQuery,
  getStreakProtectorsQuery,
  getUserHabitVersionsQuery,
  type HabitRow,
  type HabitVersionRow,
  queryKeys,
  type ReminderRow,
  type StreakProtectorRow,
} from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface BaseHabitsQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  /** Stale time in ms. Pass your app's OFFLINE_CACHE_STALE_TIME_MS. */
  staleTime?: number;
  /** Optional extra throttle in ms. Defaults to 300ms. */
  throttleTime?: number;
}

/** User habits (active, or optionally archived as well). */
export function useHabitsQuery(
  options: BaseHabitsQueryOptions & { includeArchived?: Accessor<boolean> },
) {
  return createPowerSyncQuery<HabitRow>({
    queryKey: () => queryKeys.habits.list(options.userId(), options.includeArchived?.() ?? false),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid
        ? getHabitsQuery(db, uid, { includeArchived: options.includeArchived?.() ?? false })
        : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/**
 * COMPLETE user completions history. Source for streaks,
 * calendars, and statistics (bounded dataset: 1 row/habit/day).
 */
export function useCompletionsQuery(options: BaseHabitsQueryOptions) {
  return createPowerSyncQuery<CompletionRow>({
    queryKey: () => queryKeys.habits.completions(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getAllCompletionsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/**
 * Synchronized definition versions (offline window) of ALL user
 * habits. Powers historical target resolution (version-timeline):
 * each day's "completed" status is evaluated against definition active on THAT day.
 */
export function useHabitVersionsQuery(options: BaseHabitsQueryOptions) {
  return createPowerSyncQuery<HabitVersionRow>({
    queryKey: () => queryKeys.habits.versions(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getUserHabitVersionsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Version history (offline window) for a SINGLE habit (detail). */
export function useHabitVersionsForHabitQuery(
  options: Omit<BaseHabitsQueryOptions, "userId"> & { habitId: Accessor<string | undefined> },
) {
  return createPowerSyncQuery<HabitVersionRow>({
    queryKey: () => queryKeys.habits.habitVersions(options.habitId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const hid = options.habitId();
      return db && hid ? getHabitVersionsQuery(db, hid) : null;
    },
    enabled: () => Boolean(options.habitId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** User reminders (scheduler + per-habit editing). */
export function useRemindersQuery(options: BaseHabitsQueryOptions) {
  return createPowerSyncQuery<ReminderRow>({
    queryKey: () => queryKeys.habits.reminders(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getRemindersQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/**
 * User STREAK PROTECTORS (inventory + spent). Feeds offline
 * streak engine: days with protector do not break streak.
 */
export function useStreakProtectorsQuery(options: BaseHabitsQueryOptions) {
  return createPowerSyncQuery<StreakProtectorRow>({
    queryKey: () => queryKeys.habits.protectors(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getStreakProtectorsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
